import { useEffect, useState } from "react";
import Row from "../../layout/row/row";
import {
  Alignments,
  applyFormat,
  classFromProperties,
  getOptionalProps,
  getUniqId,
  NumericDecimals,
  NumericSigned,
  NumericTypes,
  Paddings,
  removeFormat,
  Sizes,
  splitDate,
} from "../../ui-helpers";
import {
  FaEye,
  FaUser,
  FaLock,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaHashtag,
  FaDollarSign,
  FaEdit,
  FaTextHeight,
  FaGlobe,
  FaCity,
  FaBuilding,
  FaCog,
  FaTags,
  FaFlag,
  FaMapMarkerAlt,
  FaIndustry,
  FaUserTie,
  FaGraduationCap,
  FaHeart,
  FaHome,
  FaCar,
  FaPlane,
  FaShoppingCart,
  FaChartBar,
  FaFileAlt,
  FaSearch,
  FaFilter,
  FaIdCard,
  FaTag,
  FaPercentage,
  FaShieldAlt,
  FaEuroSign,  FaChartLine,
  FaHandHoldingUsd,
  FaHospital,
  FaHandshake,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import "./text-field.css";
import Button from "../button/button";

/**
 * Generates a text input component.
 * @param {ReactDOM.props} props Component properties.
 * @returns Generated component.
 */
const TextField = (props) => {
  // Excluded properties for automatic properties propagation
  const Excluded = [
    "dataType",
    "label",
    "labelStyle",
    "labelIcon",
    "value",
    "format",
    "password",
    "onChange",
    "onKeyDown",
    "onPaste",
    "onFocus",
    "onBlur",
    "style",
    "textAlign",
    "iconBefore",
    "iconAfter",
    "multiline",
    "disabled",
    "readOnly",
    "hideIconInLabel",
  ].concat(Alignments, Paddings, Sizes);

  // Data type getter
  const getDataType = () => (!props.dataType && props?.format && props.format.dataType ? props.format.dataType : props?.dataType || "string");

  // Check alignment
  let alignment = NumericTypes.includes(getDataType()) ? "right" : "left";
  if (props?.textAlign) alignment = props.textAlign;

  // Set style
  const style = props?.style || {};
  if (!style.textAlign) style.textAlign = alignment; // Fonction pour obtenir l'icône par défaut selon le type de champ
  const getDefaultIcon = () => {
    // Si une icône personnalisée est fournie pour le label, l'utiliser en priorité
    if (props?.labelIcon) return props.labelIcon;

    if (props?.iconBefore) return props.iconBefore; // Si une icône est fournie, l'utiliser

    // Icônes basées sur le type de mot de passe
    if (props?.password) return <FaLock />;

    // Icônes basées sur le label
    if (props?.label) {
      const labelLower = props.label.toLowerCase();

      // Géographie et lieux
      if (labelLower.includes("pays") || labelLower.includes("country")) return <FaGlobe />;
      if (labelLower.includes("ville") || labelLower.includes("city") || labelLower.includes("commune")) return <FaCity />;
      if (labelLower.includes("région") || labelLower.includes("region") || labelLower.includes("département")) return <FaMapMarkerAlt />;
      if (labelLower.includes("adresse") || labelLower.includes("address") || labelLower.includes("domicile")) return <FaHome />;

      // Personnes et identité
      if (labelLower.includes("prénom") || labelLower.includes("firstname")) return <FaUser />;
      if (labelLower.includes("nom") && !labelLower.includes("prénom")) return <FaIdCard />;
      if (labelLower.includes("utilisateur") || labelLower.includes("user")) return <FaUser />;
      if (labelLower.includes("civilité") || labelLower.includes("titre") || labelLower.includes("genre")) return <FaUserTie />;

      // Contact
      if (labelLower.includes("email") || labelLower.includes("mail") || labelLower.includes("courriel")) return <FaEnvelope />;
      if (labelLower.includes("téléphone") || labelLower.includes("phone") || labelLower.includes("mobile")) return <FaPhone />;      // Dates et temps
      if (labelLower.includes("date") || labelLower.includes("année") || labelLower.includes("mois") || labelLower.includes("naissance")) return <FaCalendarAlt />;
      if (labelLower.includes("espérance de vie") || labelLower.includes("esperance de vie")) return <FaHeart />;
      // Professionnel
      if (labelLower.includes("entreprise") || labelLower.includes("société") || labelLower.includes("company")) return <FaBuilding />;
      if (labelLower.includes("capital social")) return <FaBuilding />;
      if (labelLower.includes("secteur") || labelLower.includes("industrie") || labelLower.includes("activité")) return <FaIndustry />;
      if (labelLower.includes("profession") || labelLower.includes("métier") || labelLower.includes("job")) return <FaUserTie />;
      if (labelLower.includes("formation") || labelLower.includes("diplôme") || labelLower.includes("étude")) return <FaGraduationCap />;      // Financier
      if (labelLower.includes("capital décès toutes causes")) return <FaShieldAlt />;
      if (labelLower.includes("capital décès accidentel")) return <FaExclamationTriangle />;
      if (labelLower.includes("double effet")) return <FaMoneyBillWave />;
      if (labelLower.includes("frais d'obsèques")) return <FaFileInvoiceDollar />;
      if (labelLower.includes("rente conjoint")) return <FaHandshake />;
      if (labelLower.includes("abondement perco") || labelLower.includes("abondement")) return <FaHandHoldingUsd />;
      if (labelLower.includes("cotisation prévoyance") || labelLower.includes("cotisation prevoyance")) return <FaShieldAlt />;
      if (labelLower.includes("cotisation santé") || labelLower.includes("cotisation sante")) return <FaHospital />;
      if (labelLower.includes("remises brutes corrigées")) return <FaEuroSign />;
      if (labelLower.includes("bonification") || labelLower.includes("bonus") || labelLower.includes("prime")) return <FaHandHoldingUsd />;
      if (labelLower.includes("mda") || labelLower.includes("fonction publique hospitalière") || labelLower.includes("hospitalière")) return <FaHospital />;
      if (labelLower.includes("indice de fin de carrière") || labelLower.includes("ifc")) return <FaChartLine />;
      if (labelLower.includes("épargne acquise") || labelLower.includes("epargne acquise")) return <FaChartBar />;
      if (labelLower.includes("rendement net")) return <FaChartBar />;
      if (labelLower.includes("frais sur versement") || labelLower.includes("frais de versement")) return <FaPercentage />;
      if (labelLower.includes("pension brute") || labelLower.includes("pension nette")) return <FaHandHoldingUsd />;
      if (labelLower.includes("prix") || labelLower.includes("montant") || labelLower.includes("euro") || labelLower.includes("€")) return <FaDollarSign />;
      if (labelLower.includes("salaire") || labelLower.includes("revenu") || labelLower.includes("rémunération")) return <FaDollarSign />;
      if (labelLower.includes("avantages en nature") || labelLower.includes("avantage")) return <FaDollarSign />;
      if (labelLower.includes("dividendes bruts")) return <FaEuroSign />;      if (labelLower.includes("%") || labelLower.includes("pourcentage") || labelLower.includes("taux")) return <FaPercentage />;
      if (labelLower.includes("détention capitalistique")) return <FaPercentage />;      if (labelLower.includes("invalidité") || labelLower.includes("incapacité")) return <FaShieldAlt />;
      if (labelLower.includes("disponible fiscal") || labelLower.includes("disponible fiscale")) return <FaShieldAlt />;
      if (labelLower.includes("cotisation annuel à investir") || labelLower.includes("cotisation annuelle à investir")) return <FaHandHoldingUsd />;
      if (labelLower.includes("épargne brute au terme") || labelLower.includes("epargne brute au terme")) return <FaChartBar />;
      if (labelLower.includes("frais sur arrérages") || labelLower.includes("frais sur arrerages")) return <FaPercentage />;
      if (labelLower.includes("revalorisation de la rente") || labelLower.includes("revalorisation rente")) return <FaChartLine />;

      // Configuration et paramètres
      if (labelLower.includes("config") || labelLower.includes("paramètre") || labelLower.includes("option")) return <FaCog />;
      if (labelLower.includes("statut") || labelLower.includes("état") || labelLower.includes("status")) return <FaTags />;
      if (labelLower.includes("type") || labelLower.includes("catégorie") || labelLower.includes("genre")) return <FaTags />;
      if (labelLower.includes("priorité") || labelLower.includes("niveau") || labelLower.includes("grade")) return <FaFlag />;
      if (labelLower.includes("libellé") || labelLower.includes("label") || labelLower.includes("titre")) return <FaTag />;

      // Transport
      if (labelLower.includes("véhicule") || labelLower.includes("voiture") || labelLower.includes("transport")) return <FaCar />;
      if (labelLower.includes("voyage") || labelLower.includes("destination") || labelLower.includes("vol")) return <FaPlane />;

      // Autres
      if (labelLower.includes("produit") || labelLower.includes("article") || labelLower.includes("commande")) return <FaShoppingCart />;
      if (labelLower.includes("rapport") || labelLower.includes("statistique") || labelLower.includes("analyse")) return <FaChartBar />;
      if (labelLower.includes("document") || labelLower.includes("fichier") || labelLower.includes("file")) return <FaFileAlt />;
      if (labelLower.includes("recherche") || labelLower.includes("search") || labelLower.includes("chercher")) return <FaSearch />;
      if (labelLower.includes("filtre") || labelLower.includes("filter") || labelLower.includes("tri")) return <FaFilter />;
      if (labelLower.includes("favori") || labelLower.includes("préférence") || labelLower.includes("like")) return <FaHeart />;
      if (labelLower.includes("mot de passe") || labelLower.includes("password")) return <FaLock />;
    }

    // Icônes basées sur le dataType
    if (getDataType()) {
      switch (getDataType()) {
        case "int":
        case "float":
          return <FaHashtag />;
        case "date":
          return <FaCalendarAlt />;
        case "string":
          return props?.multiline ? <FaTextHeight /> : <FaEdit />;
        default:
          return <FaEdit />;
      }
    }

    // Icônes basées sur le type d'input
    if (props?.type) {
      switch (props.type) {
        case "email":
          return <FaEnvelope />;
        case "tel":
        case "phone":
          return <FaPhone />;
        case "number":
          return <FaDollarSign />;
        case "date":
          return <FaCalendarAlt />;
        default:
          return <FaUser />;
      }
    }

    return <FaEdit />;
  };

  //#region Format handling

  /**
   * Validate an input character.
   * @param {HTMLInputElement} el Input element.
   * @param {string} value Value without format.
   * @param {CharacterData} chr Character to validate.
   * @returns True if character is allowed, False otherwise.
   */
  const validateChar = (el, value, chr) => {
    // Check empty character
    if (!chr) return false;

    // Always prevent \ " < >
    if (chr === "\\" || chr === '"') return false;

    // Assert format
    if (getDataType() === "string") return true;

    // Check type
    const ascii = chr.charCodeAt(0);
    if (NumericTypes.includes(getDataType())) {
      // Check numeric value
      const unselected = value.substring(0, el.selectionStart) + value.substring(el.selectionEnd + 1);
      if (chr === "BACK") return true;
      if (ascii >= 48 && ascii <= 57) return true;
      if (
        NumericDecimals.includes(getDataType()) &&
        (chr === (props.format?.decimal || ".") || ascii === 46) &&
        unselected.indexOf(props.format?.decimal || ".") === -1 &&
        unselected.indexOf(".") === -1
      )
        return true;
      if (ascii === 45 && value.indexOf("-") === -1 && !el.selectionStart && NumericSigned.includes(getDataType())) return true;
    } else if (getDataType() === "date") {
      // Check date value
      const actualValue = el.value;
      if (chr === "BACK") {
        if (el.selectionStart !== el.selectionEnd || el.selectionStart !== actualValue.length) return true;
        if (actualValue.substr(actualValue.length - 1, 1) !== "/") return true;
        el.setSelectionRange(Math.max(0, actualValue.length - 2), actualValue.length, "backward");
      }
      if (ascii >= 48 && ascii <= 57) return true;
      const slashes = actualValue.split("/").length - 1;
      if (chr === "/" && slashes < 2 && actualValue.substr(actualValue.length - 1, 1) !== "/") return true;
    }

    // Character not accepted
    return false;
  };

  //#endregion

  //#region Keyboard events

  /**
   * Handles a key down event.
   * @param {KeyboardEvent} evt Keyboard event.
   * @returns False if event is cancelled.
   */
  const handleKeyDown = (evt) => {
    // Initialize result
    let valid = false;

    // Get value
    const el = evt.target;
    const value = removeFormat(el.value, getDataType(), props?.format);

    // Check enter key
    if (evt.key === "Enter" && el.nodeName === "INPUT") {
      // Get inputs
      const focusable = Array.prototype.slice.call(document.querySelectorAll('a[href], button:not([tabindex="-1"]), input, textarea, select, details, [tabindex]:not([tabindex="-1"])'));
      if (focusable.length) {
        let index = focusable.indexOf(el);
        if (index !== -1) {
          index = index === focusable.length - 1 ? 0 : index + 1;
          const nextInput = focusable[index];
          nextInput?.focus();
        }
      }
    }
    // Always authorize CTRL+A CTRL+C CTRL+X and CTRL+V
    else if ((evt.key === "a" || evt.key === "c" || evt.key === "x" || evt.key === "v") && evt.ctrlKey) {
      valid = true;
    }
    // Always authorize arrows and control characters
    else if (evt.which < 47 && evt.key !== "Backspace") {
      valid = true;
    } else {
      // Validate character
      valid = validateChar(el, value, evt.key === "Backspace" ? "BACK" : evt.key);
    }

    // Check if key must be cancelled
    if (!valid) {
      evt.preventDefault();
      return false;
    }

    // Call user defined handler
    return props?.onKeyDown?.(evt, value);
  };

  //#endregion

  //#region Input events

  /**
   * Handles an input change event.
   * @param {ChangeEvent} evt Change event.
   * @returns False if event is cancelled.
   */
  const handleChange = (evt) => {
    // Update value
    setValue(removeFormat(evt.target.value));
  };

  //#endregion

  //#region Focus events

  /**
   * Handles a focus event.
   * @param {FocusEvent} evt Focus event.
   * @returns False if event is cancelled.
   */
  const handleFocus = (evt) => {
    // Set focus state
    setIsFocused(true);

    // Remove format
    evt.target.value = removeFormat(value, getDataType(), props?.format);
    if (!props?.readOnly && !props?.disabled)
      // setTimeout(() => { evt.target.select() }, 10);
      evt.target.select();
    else evt.target.setSelectionRange(0, 0);

    // Call user defined handler
    return props?.onFocus?.(evt);
  };

  /**
   * Handles a blur event.
   * @param {BlurEvent} evt Blur event.
   * @returns False if event is cancelled.
   */
  const handleBlur = (evt) => {
    // Set blur state
    setIsFocused(false);

    // Apply format
    const dataType = getDataType();
    let unformatted = removeFormat(value, dataType, props?.format);

    // Check date
    if (dataType === "date") unformatted = splitDate(unformatted).replace(/\//g, "");

    // Call user defined change handler
    evt.target.value = unformatted === "" && NumericTypes.includes(dataType) ? "0" : unformatted;
    const unformattedProp = removeFormat(sanitizeValue(props?.value), dataType, props?.format);
    if (unformatted !== unformattedProp) props?.onChange?.(evt, unformatted);

    // Call user defined blur handler
    const result = props?.onBlur?.(evt, unformatted);
    evt.target.value = applyFormat(unformatted, dataType, props?.format);
    return result;
  };

  //#endregion

  //#region Clipboard events

  /**
   * Handles a paste action.
   * @param {ClipboardEvent} evt Clipboard event.
   * @returns Always false to cancel event.
   */
  const handlePaste = (evt) => {
    // Get type informations
    const el = evt.target;
    const actualValue = el.value;
    const unformatted = removeFormat(actualValue, getDataType(), props?.format);

    // Get clipboard value
    const pasted = evt.clipboardData.getData("text");
    let result = actualValue.substr(0, el.selectionStart);
    const suffix = actualValue.substr(el.selectionEnd);
    for (let i = 0; i < pasted.length; i++) {
      let chr = pasted.substring(i, i + 1);
      if (validateChar(el, unformatted, chr)) result += chr;
    }
    result += suffix;

    // Set value
    //el.value = result;
    setValue(result);

    // Cancel paste
    evt.preventDefault();
    return false;
  };

  //#endregion

  /**
   * Generates input element.
   * @returns Input element.
   */
  const generateInput = () => (
    <input
      id={id}
      className="text-field-input"
      onChange={handleChange}
      type={props?.password ? "password" : "text"}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={style}
      value={value}
      disabled={isDisabled}
      readOnly={isReadOnly}
      {...getOptionalProps(props, Excluded)}
    />
  );

  /**
   * Generates text area element.
   * @returns Textarea element.
   */
  const generateTextArea = () => (
    <textarea
      id={id}
      className="text-field-input text-field-textarea"
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={style}
      value={value}
      disabled={isDisabled}
      readOnly={isReadOnly}
      {...getOptionalProps(props, Excluded)}
    ></textarea>
  );

  /**
   * Toggles plain text view for password field.
   */
  const togglePassword = () => {
    const el = document.getElementById(id);
    el.setAttribute("type", el.getAttribute("type") === "password" ? "text" : "password");
  };
  /**
   * Generates input field.
   * @returns Generated input component
   */
  const generateField = () => {
    // Define input div class name
    let className = "input-field";
    if (props?.iconBefore) className += " with-icon-left";
    if (props?.iconAfter || props?.password) className += " with-icon-right";

    // Return row with icons
    return (
      <Row>
        {props?.iconBefore ? (
          <div className="icon icon-left" tabIndex="-1">
            {props.iconBefore}
          </div>
        ) : (
          <></>
        )}
        <div className={className} tabIndex="-1">
          {props?.multiline ? generateTextArea() : generateInput()}
          {props?.password ? (
            <div className="icon icon-right icon-inside" tabIndex="-1">
              <Button icon tabIndex="-1" onClick={togglePassword}>
                <FaEye />
              </Button>
            </div>
          ) : (
            <></>
          )}
        </div>
        {!props?.password && props?.iconAfter ? (
          <div className="icon icon-right" tabIndex="-1">
            {props.iconAfter}
          </div>
        ) : (
          <></>
        )}
      </Row>
    );
  };

  /**
   * Ensures value string even if undefined.
   * @param {string} value Value to sanitize.
   * @returns Sanitized value.
   */
  const sanitizeValue = (value) => (value !== undefined && value !== null ? value.toString() : "");

  // Initialize states
  const [id] = useState(props?.id ? props.id : getUniqId());
  const [value, setValue] = useState(getDataType() !== "date" ? sanitizeValue(props?.value) : applyFormat(sanitizeValue(props?.value), getDataType(), props?.format));
  const [isFocused, setIsFocused] = useState(false);
  const [isDisabled, setDisabled] = useState(props?.disabled !== undefined ? props?.disabled : false);
  const [isReadOnly, setReadOnly] = useState(props?.readOnly !== undefined ? props?.readOnly : false);

  /**
   * On value property change.
   */
  useEffect(() => {
    const strValue = sanitizeValue(props?.value);
    const dataType = getDataType();
    const useFormat = isFocused && dataType !== "date" ? true : false;
    setValue(useFormat ? strValue : applyFormat(strValue, dataType, props?.format));
    setDisabled(props?.disabled !== undefined ? props?.disabled : false);
    setReadOnly(props?.readOnly !== undefined ? props?.readOnly : false);
    // eslint-disable-next-line
  }, [props?.value, getDataType(), props?.format, props?.disabled, props?.readOnly, isFocused, isDisabled, isReadOnly]);

  // Generate component
  return (
    <div className={classFromProperties(props, "text-field" + (isDisabled ? " disabled" : "") + (isReadOnly ? " readOnly" : ""), Alignments, Paddings, Sizes, ["hidden"])} tabIndex="-1">
      
      {props?.label ? (
        <div className="text-field-label-row">
          {!props?.hideIconInLabel && <span className="text-field-icon">{getDefaultIcon()}</span>}
          <label tabIndex="-1" htmlFor={id} style={props?.labelStyle} className="text-field-label">
            {props.label}
          </label>
        </div>
      ) : (
        <></>
      )}
      {generateField()}
    </div>
  );
};

export default TextField;
